import { revealCaption } from './revealCaption.ts';

interface PlayingAnswer {
  itemId: string;
  /** The playback mark this answer's audio is counted from. */
  mark: number;
  receivedMs: number;
  playedMs: number;
  totalMs: number | null;
  /** Cut off by the user: the words stop where the voice stopped. */
  stopped: boolean;
  shownLength: number;
}

/**
 * The character's caption, kept in step with its voice.
 *
 * The text arrives faster than it is spoken, so it is held back and revealed
 * as the playback reports progress. A progress report names the mark it counts
 * from, so one about an earlier answer never moves the current caption.
 */
export class RealtimeCaption {
  private readonly onChange: (text: string) => void;
  private readonly texts = new Map<string, string>();
  private answer: PlayingAnswer | null = null;
  private marks = 0;
  private shown = '';

  constructor(onChange: (text: string) => void) {
    this.onChange = onChange;
  }

  /** The audio of an answer starts. Returns the mark for the playback to count from. */
  startAnswer(itemId: string): number {
    this.marks += 1;

    if (this.answer?.itemId === itemId) {
      return this.marks;
    }

    if (this.answer !== null) {
      this.texts.delete(this.answer.itemId);
    }
    this.answer = {
      itemId,
      mark: this.marks,
      receivedMs: 0,
      playedMs: 0,
      totalMs: null,
      stopped: false,
      shownLength: 0,
    };
    this.update();
    return this.marks;
  }

  addText(itemId: string, delta: string): void {
    this.texts.set(itemId, (this.texts.get(itemId) ?? '') + delta);
    if (this.answer?.itemId === itemId) {
      this.update();
    }
  }

  addAudio(ms: number): void {
    if (this.answer !== null && this.answer.totalMs === null) {
      this.answer.receivedMs += ms;
    }
  }

  played(mark: number, ms: number): void {
    if (this.answer?.mark !== mark) {
      return;
    }

    this.answer.playedMs = ms;
    this.update();
  }

  /** No more audio is coming for this answer, so its length is known. */
  complete(): void {
    if (this.answer === null || this.answer.totalMs !== null) {
      return;
    }

    this.answer.totalMs = this.answer.receivedMs;
    this.update();
  }

  /** The voice was cut off: the caption stays where it had got to. */
  stop(): void {
    if (this.answer !== null) {
      this.answer.stopped = true;
    }
  }

  private update(): void {
    const answer = this.answer;
    if (answer === null || answer.stopped) {
      return;
    }

    const text = revealCaption({
      text: this.texts.get(answer.itemId) ?? '',
      playedMs: answer.playedMs,
      totalMs: answer.totalMs,
      shownLength: answer.shownLength,
    });
    answer.shownLength = text.length;

    /* The last answer stays up until the next one has a word to show. */
    if (text === '' || text === this.shown) {
      return;
    }

    this.shown = text;
    this.onChange(text);
  }
}
